/**
 * Surfaces that fill the corner used by the floating feedback toast.
 *
 * The toast is a fixed card at the bottom-right of the viewport. Survey data
 * is a chart canvas, and twin chat (the results chat tab and the room page)
 * plus the report analyst pin a composer to that same corner. Auto feedback
 * waits until the user is on a clearer surface — summary or deck on the
 * results page, or any page that has not claimed the corner.
 *
 * A manual card on that same surface used to sit fixed at the top center.
 * That covers the Survey data counts line, the Analyst column, and the
 * twin's first chat message. It now occupies a strip above the page, and
 * the page shrinks by the same amount (`--feedback-manual-lane`).
 *
 * This is a product-wide rule, not a per-study exception.
 */

const CORNER_DENSE_STUDY_TABS = new Set<string>(['survey', 'chat', 'analyst'])

export function isCornerDenseStudyTab(tab: string): boolean {
  return CORNER_DENSE_STUDY_TABS.has(tab)
}

/** CSS variable full-bleed pages subtract so a manual card can sit above them. */
export const FEEDBACK_MANUAL_LANE_VAR = '--feedback-manual-lane'

/** Matches the toast's `w-[320px]`. */
export const FEEDBACK_TOAST_WIDTH_PX = 320

/** `top-20` — the old fixed top-center manual card. */
export const FEEDBACK_TOP_ANCHOR_OFFSET_PX = 80

export interface FeedbackBox {
  x: number
  y: number
  width: number
  height: number
}

export function boxesIntersect(a: FeedbackBox, b: FeedbackBox): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  )
}

export type OccupiedManualAnchor = 'top' | 'lane'

/**
 * Viewport box for a manual card while a dense surface holds the corner.
 *
 * `top` is `fixed top-20 left-1/2 w-[320px] -translate-x-1/2`.
 * `lane` is the strip at the top of the content column, from the sidebar
 * edge to the right edge of the viewport. The page starts below it.
 */
export function occupiedManualFeedbackBox(args: {
  anchor: OccupiedManualAnchor
  viewportWidth: number
  sidebarWidth: number
  toastHeight: number
}): FeedbackBox {
  if (args.anchor === 'top') {
    return {
      x: (args.viewportWidth - FEEDBACK_TOAST_WIDTH_PX) / 2,
      y: FEEDBACK_TOP_ANCHOR_OFFSET_PX,
      width: FEEDBACK_TOAST_WIDTH_PX,
      height: args.toastHeight,
    }
  }
  return {
    x: args.sidebarWidth,
    y: 0,
    width: Math.max(0, args.viewportWidth - args.sidebarWidth),
    height: args.toastHeight,
  }
}

export interface DenseSurfaceReadingRegions {
  surveyCountsLine: FeedbackBox
  analystContent: FeedbackBox
  twinFirstMessage: FeedbackBox
}

/**
 * The three regions a manual card must leave readable while the corner is
 * claimed. `contentTop` is 0 when the card overlays the page, and the lane
 * height when the page has been moved down under the strip.
 *
 * Survey counts line: first row of Survey data, under the report header
 * (`py-4`) and the tab's `py-6`, across the tab width.
 * Analyst content: the centered `max-w-3xl` column under that header, down
 * to the composer.
 * Twin first message: the first bubble under the chat header (`pt-8 pb-3`
 * plus the message list's `py-4`), inside the same centered column.
 */
export function denseSurfaceReadingRegions(args: {
  viewportWidth: number
  viewportHeight: number
  sidebarWidth: number
  contentTop: number
}): DenseSurfaceReadingRegions {
  const contentX = args.sidebarWidth
  const contentWidth = Math.max(0, args.viewportWidth - args.sidebarWidth)
  const columnWidth = Math.min(768, contentWidth)
  const columnX = contentX + (contentWidth - columnWidth) / 2
  const reportHeader = 72
  const surveyPad = 24
  const chatHeader = 96
  const composerReserve = 120

  return {
    surveyCountsLine: {
      x: contentX + 32,
      y: args.contentTop + reportHeader + surveyPad,
      width: Math.max(0, contentWidth - 64),
      height: 28,
    },
    analystContent: {
      x: columnX,
      y: args.contentTop + reportHeader,
      width: columnWidth,
      height: Math.max(
        0,
        args.viewportHeight - (args.contentTop + reportHeader) - composerReserve,
      ),
    },
    twinFirstMessage: {
      x: columnX + 16,
      y: args.contentTop + chatHeader,
      width: Math.max(0, columnWidth - 32),
      height: 72,
    },
  }
}

export function readingRegionsCoveredBy(
  anchor: OccupiedManualAnchor,
  frame: { viewportWidth: number; viewportHeight: number; sidebarWidth: number; toastHeight: number },
): Array<keyof DenseSurfaceReadingRegions> {
  const toast = occupiedManualFeedbackBox({
    anchor,
    viewportWidth: frame.viewportWidth,
    sidebarWidth: frame.sidebarWidth,
    toastHeight: frame.toastHeight,
  })
  const contentTop = anchor === 'lane' ? frame.toastHeight : 0
  const regions = denseSurfaceReadingRegions({
    viewportWidth: frame.viewportWidth,
    viewportHeight: frame.viewportHeight,
    sidebarWidth: frame.sidebarWidth,
    contentTop,
  })
  return (Object.keys(regions) as Array<keyof DenseSurfaceReadingRegions>).filter((key) =>
    boxesIntersect(toast, regions[key]),
  )
}

export interface ArmedAutoFeedback {
  event: string
  /** Epoch ms when the dwell delay has elapsed and the toast may open. */
  dueAt: number
  /**
   * True once the prompt has been held because the corner was occupied.
   * Navigation away from a clear surface still cancels a prompt that was
   * never held. A held prompt survives that navigation and opens on the
   * next clear surface.
   */
  heldForCorner: boolean
}
